import {
  Link,
  Outlet,
  useLocation,
  useMatches,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import {
  BookOpen,
  Building2,
  CircleHelp,
  FileSearch,
  Focus,
  FolderKanban,
  LayoutDashboard,
  ScrollText,
  Search,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  UserRound,
  Users,
  Wrench,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { neutralPointer } from '../features/discovery/collectorLabel.ts';
import { Banner } from '../components/Banner.tsx';
import { Button } from '../components/Button.tsx';
import { ModalDialog } from '../components/Dialog.tsx';
import { shortId } from '../components/format.ts';
import { isAttemptActive } from '../features/discovery/model.ts';
import { LedgerCanvas } from '../components/LedgerCanvas.tsx';
import { PreferencesMenu } from '../components/PreferencesMenu.tsx';
import { TraceMark } from '../components/TraceMark.tsx';
import { TraceLockup } from '../components/TraceLockup.tsx';
import { roleLabel } from '../features/auth/permissions.ts';
import { onboarding } from '../features/onboarding/actions.ts';
import { useWorkspaceFindings } from '../features/findings/store.ts';
import { api } from '../features/onboarding/api.ts';
import { announce } from '../state/announcer.ts';
import { useSession } from '../state/session.ts';
import { MandateBanner } from '../features/authorization/MandateBanner.tsx';
import { CommandPalette, type CommandPaletteItem } from './CommandPalette.tsx';
import { refreshDirectory } from './directory.ts';
import { AccountMenu, OrganizationSwitcher, WorkspaceSwitcher } from './ShellMenus.tsx';
import styles from './AppShell.module.css';

// Default primary navigation (DESIGN_SYSTEM.md §4.1).
const destinations = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, id: 'overview' },
  { to: '/findings', label: 'Findings', icon: ShieldAlert, id: 'findings' },
  { to: '/remediations', label: 'Remediations', icon: Wrench, id: 'remediations' },
  { to: '/engagements', label: 'Engagements', icon: FolderKanban, id: 'engagements' },
  { to: '/evidence', label: 'Evidence', icon: FileSearch, id: 'evidence' },
  { to: '/reports', label: 'Reports', icon: BookOpen, id: 'reports' },
  { to: '/authorization', label: 'Authorization', icon: ShieldCheck, id: 'authorization' },
  { to: '/audit-logs', label: 'Audit logs', icon: ScrollText, id: 'audit-logs' },
] as const;

const supportPages = [
  {
    id: 'settings',
    to: '/settings',
    label: 'Settings',
    description: 'Your account, this workspace and its organization',
    icon: Settings2,
    keywords: ['members', 'organization', 'profile', 'preferences'],
  },
  {
    id: 'help',
    to: '/help',
    label: 'Help',
    description: 'Workspace guidance and capability status',
    icon: CircleHelp,
    keywords: ['guide', 'support'],
  },
] as const;

// The audit trail, settings and help close the rail; the working pages stay on top.
const railEnd = new Set<string>(['/audit-logs']);
const railEndPages = [...destinations.filter(({ to }) => railEnd.has(to)), ...supportPages];

// Search also opens each settings page directly.
const settingsPages = [
  {
    id: 'settings-profile',
    to: '/settings/profile',
    label: 'Profile',
    description: 'Your display name and sign-in email',
    icon: UserRound,
    keywords: ['account', 'name'],
  },
  {
    id: 'settings-preferences',
    to: '/settings/preferences',
    label: 'Preferences',
    description: 'Theme, density and motion',
    icon: Settings2,
    keywords: ['theme', 'dark', 'light', 'density', 'motion', 'animation'],
  },
  {
    id: 'settings-workspace',
    to: '/settings/workspace',
    label: 'Workspace settings',
    description: 'Name, domain and workspace ID',
    icon: Settings2,
    keywords: ['rename', 'domain'],
  },
  {
    id: 'settings-members',
    to: '/settings/members',
    label: 'Members',
    description: 'Invite people and change their roles',
    icon: Users,
    keywords: ['invite', 'roles', 'access', 'team', 'people'],
  },
  {
    id: 'settings-organization',
    to: '/settings/organization',
    label: 'Organization',
    description: 'Its name and workspaces',
    icon: Building2,
    keywords: ['org', 'company', 'workspaces'],
  },
  {
    id: 'settings-organization-members',
    to: '/settings/organization/members',
    label: 'Organization members',
    description: 'Who manages every workspace in the organization',
    icon: Users,
    keywords: ['org', 'invite', 'admins', 'owners'],
  },
] as const;

type ShellPath = (typeof destinations)[number]['to'] | (typeof supportPages)[number]['to'];

// Keyboard model: "g" then a letter jumps to a destination; "?" lists everything.
const GO_TARGETS: Record<string, ShellPath> = {
  o: '/',
  f: '/findings',
  m: '/remediations',
  e: '/engagements',
  v: '/evidence',
  r: '/reports',
  a: '/authorization',
  l: '/audit-logs',
  s: '/settings',
  h: '/help',
};
const SHORTCUTS: readonly (readonly [string, string])[] = [
  ['⌘ K / Ctrl K', 'Search records and pages'],
  ['?', 'Show this list'],
  ['g then o', 'Overview'],
  ['g then f', 'Findings'],
  ['g then m', 'Remediations'],
  ['g then e', 'Engagements'],
  ['g then v', 'Evidence'],
  ['g then r', 'Reports'],
  ['g then a', 'Authorization'],
  ['g then l', 'Audit logs'],
  ['j / k or ↓ / ↑', 'Next or previous finding row'],
  ['Enter or o', 'Open the focused finding'],
  ['i', 'Inspect the focused finding'],
  ['Esc', 'Close a dialog or leave focus mode'],
];

// Palette descriptions only; the shared StateLabel renders the severity in the UI.
const severityLabel = (severity: string) =>
  severity === 'UNKNOWN' ? 'Unknown severity' : severity[0] + severity.slice(1).toLowerCase();

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  );
}

interface FocusModeSnapshot {
  opener: HTMLElement | null;
  scrollX: number;
  scrollY: number;
  scrollPositions: Array<{ element: HTMLElement; left: number; top: number }>;
  inputSelection?: {
    element: HTMLInputElement | HTMLTextAreaElement;
    start: number;
    end: number;
    direction: 'forward' | 'backward' | 'none';
  };
}

function captureFocusModeSnapshot(fallbackOpener: HTMLElement | null): FocusModeSnapshot {
  const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  let inputSelection: FocusModeSnapshot['inputSelection'];

  if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
    const start = active.selectionStart;
    const end = active.selectionEnd;
    if (start !== null && end !== null) {
      inputSelection = {
        element: active,
        start,
        end,
        direction: active.selectionDirection ?? 'none',
      };
    }
  }

  const scrollPositions = Array.from(document.querySelectorAll<HTMLElement>('*'))
    .filter((element) => {
      if (
        element.scrollHeight <= element.clientHeight &&
        element.scrollWidth <= element.clientWidth
      ) {
        return false;
      }
      const { overflowX, overflowY } = window.getComputedStyle(element);
      return /auto|scroll|overlay/.test(`${overflowX} ${overflowY}`);
    })
    .map((element) => ({ element, left: element.scrollLeft, top: element.scrollTop }));

  return {
    opener: active && active !== document.body ? active : fallbackOpener,
    scrollX: window.scrollX,
    scrollY: window.scrollY,
    scrollPositions,
    inputSelection,
  };
}

function restoreInputSelection(snapshot: FocusModeSnapshot) {
  const inputSelection = snapshot.inputSelection;
  if (inputSelection?.element.isConnected) {
    try {
      inputSelection.element.setSelectionRange(
        inputSelection.start,
        inputSelection.end,
        inputSelection.direction,
      );
    } catch {
      // Some input types do not support a text selection.
    }
  }
}

function useRouteTitle() {
  const matches = useMatches();
  const title = [...matches].reverse().find((m) => m.staticData.title)?.staticData.title;
  const mainRef = useRef<HTMLElement>(null);
  const first = useRef(true);

  useEffect(() => {
    document.title = title ? `${title} · Traceira` : 'Traceira';
    // Move focus to the new page on client-side navigation, not on first load.
    if (first.current) first.current = false;
    else {
      mainRef.current?.focus({ preventScroll: true });
      // The page area scrolls inside the shell on laptops, so the router's window scroll reset misses it.
      mainRef.current?.scrollTo?.({ top: 0 });
    }
  }, [title]);

  return mainRef;
}

export function AppShell() {
  const workspace = useSession((s) => s.workspace);
  const workspaces = useSession((s) => s.workspaces);
  const notice = useSession((s) => s.notice);
  // Organization pages only for its members; someone given just this workspace has none.
  const hasOrganization = useSession((s) =>
    s.orgs.some((org) => org.id === s.workspace?.organization?.id),
  );
  const navigate = useNavigate();
  const router = useRouter();
  const route = useLocation();
  const mainRef = useRouteTitle();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const paletteOpenRef = useRef(paletteOpen);
  const paletteOpenerRef = useRef<HTMLElement | null>(null);
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  const focusToggleRef = useRef<HTMLButtonElement>(null);
  const pendingFocusSnapshotRef = useRef<FocusModeSnapshot | null>(null);
  const focusSnapshotRef = useRef<FocusModeSnapshot | null>(null);
  const restoreFrameRef = useRef<number | null>(null);
  const focusTimerRef = useRef<number | null>(null);

  const openPalette = () => {
    if (focusTimerRef.current !== null) {
      window.clearTimeout(focusTimerRef.current);
      focusTimerRef.current = null;
    }
    if (!paletteOpenRef.current) {
      const focused = document.activeElement;
      paletteOpenerRef.current =
        focused instanceof HTMLElement && focused !== document.body
          ? focused
          : searchTriggerRef.current;
    }
    paletteOpenRef.current = true;
    refreshDirectory();
    setPaletteOpen(true);
  };

  const closePalette = (restoreFocus = true) => {
    if (focusTimerRef.current !== null) {
      window.clearTimeout(focusTimerRef.current);
      focusTimerRef.current = null;
    }
    const opener = paletteOpenerRef.current;
    paletteOpenerRef.current = null;
    paletteOpenRef.current = false;
    setPaletteOpen(false);
    if (!restoreFocus || !opener?.isConnected) return;

    const reducedMotion =
      document.documentElement.dataset.motion === 'reduced' ||
      (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    focusTimerRef.current = window.setTimeout(
      () => {
        opener.focus({ preventScroll: true });
        focusTimerRef.current = null;
      },
      reducedMotion ? 0 : 130,
    );
  };

  const exitFocusMode = useCallback(() => {
    const snapshot = focusSnapshotRef.current;
    setFocusMode(false);
    announce('Focus mode off. Your previous view is restored.');
    if (!snapshot) return;

    if (restoreFrameRef.current !== null) {
      window.cancelAnimationFrame(restoreFrameRef.current);
    }
    restoreFrameRef.current = window.requestAnimationFrame(() => {
      restoreFrameRef.current = window.requestAnimationFrame(() => {
        window.scrollTo(snapshot.scrollX, snapshot.scrollY);
        for (const position of snapshot.scrollPositions) {
          if (!position.element.isConnected) continue;
          position.element.scrollLeft = position.left;
          position.element.scrollTop = position.top;
        }

        const opener = snapshot.opener?.isConnected ? snapshot.opener : mainRef.current;
        opener?.focus({ preventScroll: true });
        restoreInputSelection(snapshot);
        focusSnapshotRef.current = null;
        restoreFrameRef.current = null;
      });
    });
  }, [mainRef]);

  const toggleFocusMode = () => {
    if (focusMode) {
      exitFocusMode();
      return;
    }

    if (restoreFrameRef.current !== null) {
      window.cancelAnimationFrame(restoreFrameRef.current);
      restoreFrameRef.current = null;
    }
    focusSnapshotRef.current =
      pendingFocusSnapshotRef.current ?? captureFocusModeSnapshot(focusToggleRef.current);
    setFocusMode(true);
    announce('Focus mode on. Workspace context stays visible. Press Escape to exit.');
  };

  useEffect(() => {
    let goPressedAt = 0;
    const handleShortcut = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target)) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openPalette();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === '?') {
        event.preventDefault();
        setShortcutsOpen(true);
        return;
      }
      // Single-key chords stay out of dialogs and the palette.
      if (
        paletteOpenRef.current ||
        (event.target instanceof Element && event.target.closest('[role="dialog"]'))
      ) {
        return;
      }
      if (event.key === 'g') {
        goPressedAt = Date.now();
        return;
      }
      const target = GO_TARGETS[event.key];
      if (target && Date.now() - goPressedAt < 1500) {
        goPressedAt = 0;
        event.preventDefault();
        void navigate({ to: target });
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => {
      window.removeEventListener('keydown', handleShortcut);
      if (focusTimerRef.current !== null) {
        window.clearTimeout(focusTimerRef.current);
      }
      if (restoreFrameRef.current !== null) {
        window.cancelAnimationFrame(restoreFrameRef.current);
      }
    };
  }, [navigate]);

  useEffect(() => {
    if (!focusMode) return;
    pendingFocusSnapshotRef.current = null;

    const handleFocusModeExit = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || paletteOpenRef.current) return;
      if (
        event.target instanceof Element &&
        event.target.closest('[role="dialog"], [aria-modal="true"]')
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      exitFocusMode();
    };

    document.addEventListener('keydown', handleFocusModeExit, true);
    return () => document.removeEventListener('keydown', handleFocusModeExit, true);
  }, [focusMode, exitFocusMode]);

  useEffect(
    () =>
      router.subscribe('onBeforeNavigate', () => {
        if (focusTimerRef.current !== null) {
          window.clearTimeout(focusTimerRef.current);
          focusTimerRef.current = null;
        }
        paletteOpenerRef.current = null;
        paletteOpenRef.current = false;
        setPaletteOpen(false);
        if (restoreFrameRef.current !== null) {
          window.cancelAnimationFrame(restoreFrameRef.current);
          restoreFrameRef.current = null;
        }
        focusSnapshotRef.current = null;
        pendingFocusSnapshotRef.current = null;
        setFocusMode(false);
      }),
    [router],
  );

  const signOutSession = useSession((s) => s.signOut);

  // Command glide (DESIGN_SYSTEM.md §13.2): the palette searches the loaded workspace records
  // by title, asset and ID, then the destinations. Selecting only navigates.
  const findings = useWorkspaceFindings((s) => s.findings);
  const evidence = useWorkspaceFindings((s) => s.evidence);
  const engagementLinks = useWorkspaceFindings((s) => s.findingEngagementLinks);
  const attempt = useWorkspaceFindings((s) => s.attempt);
  const discoveryRunning = isAttemptActive(attempt ?? null);
  const paletteItems = useMemo<CommandPaletteItem[]>(
    () => [
      ...findings.map((finding) => ({
        id: `finding-${finding.findingId}`,
        label: finding.title,
        description: `${severityLabel(finding.normalizedSeverity)} · ${finding.affectedAssets?.[0] ?? ''} · ${shortId(finding.findingId)}`,
        group: 'Findings' as const,
        icon: ShieldAlert,
        keywords: [finding.findingId, ...(finding.affectedAssets ?? []), finding.lane ?? ''],
        run: () =>
          navigate({ to: '/findings/$findingId', params: { findingId: finding.findingId } }),
      })),
      ...engagementLinks.map((link) => ({
        id: `engagement-${link.engagementId}`,
        label:
          findings.find((finding) => finding.findingId === link.findingId)?.title ??
          `Engagement ${shortId(link.engagementId)}`,
        description: `Engagement · ${link.derivedScopeDomains.join(', ')}`,
        group: 'Engagements' as const,
        icon: FolderKanban,
        keywords: [link.engagementId, link.findingId],
        run: () =>
          navigate({
            to: '/engagements/$engagementId',
            params: { engagementId: link.engagementId },
          }),
      })),
      ...evidence.map((record) => ({
        id: `evidence-${record.evidenceId}`,
        label: record.summary ?? neutralPointer(record.sourcePointer),
        description: `${record.kind.toLowerCase().replace('_', ' ')} · ${shortId(record.evidenceId)}`,
        group: 'Evidence' as const,
        icon: FileSearch,
        keywords: [record.evidenceId, neutralPointer(record.sourcePointer)],
        run: () =>
          navigate({ to: '/evidence', search: { inspector: 'evidence', id: record.evidenceId } }),
      })),
      ...destinations.map((destination) => ({
        id: destination.id,
        label: destination.label,
        description: `Open ${destination.label.toLowerCase()}`,
        group: 'Workspace' as const,
        icon: destination.icon,
        run: () => navigate({ to: destination.to }),
      })),
      ...settingsPages
        .filter((page) => hasOrganization || !page.to.startsWith('/settings/organization'))
        .map((page) => ({
          id: page.id,
          label: page.label,
          description: page.description,
          group: 'Settings' as const,
          icon: page.icon,
          keywords: page.keywords,
          run: () => navigate({ to: page.to }),
        })),
      ...supportPages.map((page) => ({
        id: page.id,
        label: page.label,
        description: page.description,
        group: 'Support' as const,
        icon: page.icon,
        keywords: page.keywords,
        run: () => navigate({ to: page.to }),
      })),
      ...workspaces
        .filter((ws) => ws.id !== workspace?.id)
        .map((ws) => ({
          id: `workspace-${ws.id}`,
          label: ws.name,
          description: [ws.organizationName, roleLabel(ws.role), ws.domain]
            .filter(Boolean)
            .join(' · '),
          group: 'Switch workspace' as const,
          icon: Building2,
          keywords: [ws.domain, ws.organizationName ?? '', 'switch'],
          run: () =>
            import('../features/workspace/switchWorkspace.ts').then((m) =>
              m.switchWorkspace(router, ws.id),
            ),
        })),
    ],
    [findings, engagementLinks, evidence, hasOrganization, navigate, workspaces, workspace, router],
  );

  const handleSignOut = async () => {
    try {
      const res = await api.signOut();
      if (res?.logoutUrl && typeof window !== 'undefined' && window.location) {
        signOutSession();
        announce('Signed out.');
        window.location.assign(res.logoutUrl);
        return;
      }
    } catch {
      // Ignore network errors during sign out
    }
    signOutSession();
    announce('Signed out.');
    void navigate({ to: '/sign-in', replace: true });
  };

  // A new workspace starts setup from the beginning, in this workspace's organization; this one
  // stays open, and Cancel returns to it.
  const startOver = () => {
    onboarding.reset();
    void navigate({ to: '/setup', search: { step: '1', org: workspace?.organization?.id } });
    announce('Setting up a new workspace.');
  };

  return (
    <div className={`${styles.shell} ${focusMode ? styles.focusMode : ''}`}>
      <a className={styles.skipLink} href="#main">
        Skip to content
      </a>

      <header className={styles.topbar}>
        <div className={styles.lead}>
          <Link to="/" className={styles.brand} aria-label="Traceira overview">
            {focusMode ? (
              <TraceMark size={24} />
            ) : (
              <>
                <TraceMark size={28} className={styles.brandMark} />
                <TraceLockup variant="tile" height={28} ariaHidden className={styles.brandLockup} />
              </>
            )}
          </Link>
          <OrganizationSwitcher />
          <WorkspaceSwitcher onNewWorkspace={startOver} />
        </div>
        {discoveryRunning && (
          <Link to="/" className={styles.running}>
            <span className={styles.runningDot} aria-hidden="true" />
            <span className={styles.runningLabel}>Discovery running</span>
          </Link>
        )}
        <div className={styles.tools}>
          <Button
            variant="secondary"
            size="small"
            aria-label="Search"
            ref={searchTriggerRef}
            className={styles.searchTrigger}
            onPress={openPalette}
          >
            <Search size={17} aria-hidden="true" />
            <span className={styles.searchLabel}>Search</span>
            <kbd className={styles.shortcut} aria-hidden="true">
              {typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
                ? '⌘ K'
                : 'Ctrl K'}
            </kbd>
          </Button>
          <Button
            ref={focusToggleRef}
            variant="secondary"
            size="small"
            aria-label={focusMode ? 'Exit focus mode' : 'Enter focus mode'}
            aria-pressed={focusMode}
            className={styles.focusToggle}
            onPointerDownCapture={() => {
              if (!focusMode && !pendingFocusSnapshotRef.current) {
                pendingFocusSnapshotRef.current = captureFocusModeSnapshot(focusToggleRef.current);
              }
            }}
            onPointerCancel={() => (pendingFocusSnapshotRef.current = null)}
            onPress={toggleFocusMode}
          >
            <Focus size={16} aria-hidden="true" />
            <span className={styles.focusLabel}>{focusMode ? 'Exit' : 'Focus mode'}</span>
          </Button>
          <AccountMenu onSignOut={() => void handleSignOut()} />
        </div>
      </header>

      <nav className={styles.nav} aria-label="Primary" hidden={focusMode}>
        <ul>
          {destinations
            .filter(({ to }) => !railEnd.has(to))
            .map(({ to, label, icon: Icon }) => (
              <li
                key={to}
                // The phone bar holds six tabs; these stay reachable from findings and search.
                className={
                  to === '/authorization' || to === '/remediations' ? styles.desktopOnly : undefined
                }
              >
                <Link
                  to={to}
                  className={styles.navLink}
                  activeOptions={{ exact: to === '/' }}
                  activeProps={{ 'aria-current': 'page' }}
                >
                  <Icon size={20} aria-hidden="true" />
                  <span>{label}</span>
                </Link>
              </li>
            ))}
          <li className={styles.mobileMore}>
            <PreferencesMenu presentation="mobile" />
          </li>
        </ul>
        {/* Closes the rail on laptops; phones reach these from More. */}
        <ul className={styles.navEnd}>
          {railEndPages.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <Link to={to} className={styles.navLink} activeProps={{ 'aria-current': 'page' }}>
                <Icon size={20} aria-hidden="true" />
                <span>{label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <main id="main" ref={mainRef} tabIndex={-1} className={styles.main}>
        <LedgerCanvas isOverview={route.pathname === '/'} />
        <div className={styles.content}>
          {notice && (
            <Banner
              tone="attention"
              icon="warning"
              title={notice.title}
              action={
                <Button
                  variant="tertiary"
                  size="small"
                  onPress={() => useSession.getState().setNotice(null)}
                >
                  Dismiss
                </Button>
              }
            >
              {notice.message}
            </Banner>
          )}
          <MandateBanner />
          {/* Another workspace starts its pages afresh, so nothing of this one shows. */}
          <Outlet key={workspace?.id} />
        </div>
      </main>
      <CommandPalette
        isOpen={paletteOpen}
        onOpenChange={(open) => (open ? openPalette() : closePalette())}
        items={paletteItems}
        onSelect={(item) => {
          closePalette(false);
          void item.run().then(() => {
            window.requestAnimationFrame(() => mainRef.current?.focus({ preventScroll: true }));
          });
        }}
      />
      <ModalDialog
        title="Keyboard shortcuts"
        isOpen={shortcutsOpen}
        onOpenChange={setShortcutsOpen}
      >
        <dl className={styles.shortcutList}>
          {SHORTCUTS.map(([keys, label]) => (
            <div key={label}>
              <dt>
                <kbd className={styles.shortcut}>{keys}</kbd>
              </dt>
              <dd>{label}</dd>
            </div>
          ))}
        </dl>
      </ModalDialog>
    </div>
  );
}
